import { XIcon } from 'lucide-react';
import { useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Email addresses as chips. Enter, comma, space or blur adds; pasting a list splits it; invalid entries are kept in the box and flagged. */
export function EmailChips({ value, onChange, id, placeholder = 'name@company.com', disabled }: { value: string[]; onChange: (emails: string[]) => void; id?: string; placeholder?: string; disabled?: boolean }) {
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);

  const commit = (raw: string): boolean => {
    const parts = raw
      .split(/[,\s;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length === 0) return true;
    const bad = parts.filter((e) => !EMAIL.test(e));
    const good = parts.filter((e) => EMAIL.test(e) && !value.includes(e));
    if (good.length) onChange([...value, ...good]);
    setDraft(bad.join(', '));
    setInvalid(bad.length > 0);
    return bad.length === 0;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === ';') {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      } else if (e.key === 'Enter') e.preventDefault();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) onChange(value.slice(0, -1));
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (/[,\s;]/.test(text)) {
      e.preventDefault();
      commit(`${draft} ${text}`);
    }
  };

  return (
    <div className="grid gap-1.5">
      <div className={cn('border-input dark:bg-input/30 focus-within:border-ring focus-within:ring-ring/50 flex min-h-9 flex-wrap items-center gap-1.5 border px-2 py-1.5 focus-within:ring-[3px]', invalid && 'border-destructive', disabled && 'opacity-50')}>
        {value.map((t) => (
          <Badge key={t} variant="secondary" className="gap-1 pr-1 font-normal">
            {t}
            <button type="button" aria-label={`Remove ${t}`} className="hover:text-destructive" onClick={() => onChange(value.filter((x) => x !== t))} disabled={disabled}>
              <XIcon className="size-3" />
            </button>
          </Badge>
        ))}
        <input
          id={id}
          type="email"
          multiple
          autoComplete="off"
          className="placeholder:text-muted-foreground min-w-[12rem] flex-1 bg-transparent text-sm outline-none"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => commit(draft)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          aria-invalid={invalid || undefined}
        />
      </div>
      {invalid && (
        <p role="alert" className="text-destructive text-xs">
          That does not look like an email address.
        </p>
      )}
    </div>
  );
}
